// Shared booking-meta parsers used by the wp-import-batch edge function and unit tests.
// Keep this file dependency-free so it can be imported from Deno (edge) and Vitest (node).

export const safeISO = (v: unknown): string | null => {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s);
  let d: Date;
  if (isFinite(n) && n > 1e9) d = new Date(n > 1e12 ? n : n * 1000);
  else d = new Date(s.includes("T") ? s : s.replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d.toISOString();
};

// Parse human-friendly dates like "December 5, 2024 2:00 pm" or "5th Dec 2024 at 2pm"
export const parseHumanDate = (v: unknown): string | null => {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;
  // strip ordinal suffixes: 1st, 2nd, 3rd, 4th
  s = s.replace(/(\d+)(st|nd|rd|th)\b/gi, "$1");
  // normalize separators
  s = s.replace(/\s+at\s+/gi, " ").replace(/\s+@\s+/g, " ").replace(/\s+/g, " ");
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

// Parse human duration like "2 hours", "30 minutes", "1 day", "1.5 hrs"
export const parseDurationMs = (v: unknown): number | null => {
  if (v == null) return null;
  const s = String(v).trim().toLowerCase();
  const m = s.match(/([\d.]+)\s*(day|hour|hr|min|minute)s?/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!isFinite(n)) return null;
  const unit = m[2];
  if (unit.startsWith("day")) return n * 86400_000;
  if (unit.startsWith("hour") || unit === "hr") return n * 3600_000;
  return n * 60_000;
};

export type MetaItem = { key?: string; display_key?: string; value?: any; display_value?: any };

export const findDisplay = (meta: MetaItem[], patterns: RegExp[]): string | null => {
  for (const m of meta) {
    const k = String(m.display_key ?? m.key ?? "");
    if (!k) continue;
    if (patterns.some((p) => p.test(k))) {
      const v = m.display_value ?? m.value;
      if (v != null && String(v).trim()) return String(v);
    }
  }
  return null;
};

export const findMeta = (meta: MetaItem[], keys: string[]): any => {
  return meta.find((m) =>
    keys.some((k) =>
      (m.key && m.key.toLowerCase().includes(k)) ||
      (m.display_key && m.display_key.toLowerCase().includes(k))
    )
  )?.value;
};

export type ExtractResult = {
  startISO: string | null;
  endISO: string | null;
  persons: string | null;
  reason: string | null;
};

/** Try every fallback strategy to extract a booking window from line-item meta. */
export function extractBookingWindow(meta: MetaItem[]): ExtractResult {
  let startISO: string | null = null;
  let endISO: string | null = null;

  const packed = findMeta(meta, ["yith_booking_data"]);
  try {
    let data: any = null;
    if (packed && typeof packed === "string" && packed.trim().startsWith("{")) {
      data = JSON.parse(packed);
    } else if (packed && typeof packed === "object") {
      data = packed;
    }
    if (data) {
      startISO = safeISO(data.from);
      endISO = safeISO(data.to);
      // duration is in hours per YITH schema; use as fallback for end
      if (startISO && !endISO && data.duration != null) {
        const hrs = Number(data.duration);
        if (isFinite(hrs) && hrs > 0) {
          endISO = new Date(new Date(startISO).getTime() + hrs * 3600_000).toISOString();
        }
      }
    }
  } catch { /* ignore */ }

  if (!startISO) startISO = safeISO(findMeta(meta, ["booking_from", "_from", "start"]));
  if (!endISO) endISO = safeISO(findMeta(meta, ["booking_to", "_to", "end"]));

  if (!startISO) {
    startISO = parseHumanDate(findDisplay(meta, [
      /booking\s*from/i, /^from$/i, /\bstart\b/i, /check[\s-]?in/i, /arrival/i,
    ]));
  }
  if (!endISO) {
    endISO = parseHumanDate(findDisplay(meta, [
      /booking\s*to/i, /^to$/i, /\bend\b/i, /check[\s-]?out/i, /departure/i,
    ]));
  }
  if (startISO && !endISO) {
    const durStr = findDisplay(meta, [/duration/i, /length/i]);
    const durMs = parseDurationMs(durStr);
    if (durMs) endISO = new Date(new Date(startISO).getTime() + durMs).toISOString();
  }

  const persons = findDisplay(meta, [/persons?/i, /people/i, /guests?/i, /attendees?/i]);
  const reason = !startISO ? "no_start" : !endISO ? "no_end" : null;
  return { startISO, endISO, persons, reason };
}